import React, { useEffect, useState, useRef } from 'react';
import { TopMenu } from '@layouts/Header/components/TopMenu';
import { enableScroll, disableScroll } from '@helpers/noScroll';
import { setScrollbarWidthForCSS } from '@contexts/ScrollContext';
import { Brand } from './components/Brand';
import { MenuSection } from './components/MenuSection';
import { CloseButton } from '@components/CloseButton';
import { MegaMenuMain, MainNavigationMobileNew } from '@layouts/MegaMenu';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { useStickyHeader } from './Header.hooks';
import { SearchBar } from '@components/SearchBar';
import { SignInDropdown } from '@components/SignInDropdown';
import './Header.scss';
import { HeaderBar } from '@layouts/HeaderBar';
import { CountrySelector } from '@layouts/CountrySelector';
import {
  useFetchMegaMenuNavigationQuery,
  useFetchSubNavigationQuery,
  useFetchTopMenuQuery,
} from './Header.service';
import { activeClass } from './Header.utils';
import { useWinSize } from '@contexts/WinSizeContext';
import classNames from 'classnames';

interface HeaderProps {
  lite?: boolean;
}

const Header = ({ lite = false }: HeaderProps) => {
  const cls = lite ? 'l-header-lite' : 'l-header';
  const headerRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<string>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>(null);

  const { data: megaMenuNavigation, isLoading: isLoadingMegaMenuNavigation } =
    useFetchMegaMenuNavigationQuery();
  const { data: subNavigation } = useFetchSubNavigationQuery();
  const { data: topMenu } = useFetchTopMenuQuery();

  const showDropDown = !!selectedCategory;
  const showMegaMenu = selectedCategory === 'MegaMenu';
  const showSearchArea = selectedCategory === 'SearchArea';
  const showSignInArea = selectedCategory === 'SignInArea';
  const showMarketArea = selectedCategory === 'MarketArea';
  const { isDesktopForHeader: isDesktop } = useWinSize();
  const { isMobileForHeader: isMobileScreen } = useWinSize();

  const hasBreadcrumb = subNavigation?.SubNavType === 'Breadcrumb';
  const showHeaderBar = !lite && (hasBreadcrumb || subNavigation?.SubNavType === 'SubMenu');
  const { stickyCss, handleScroll } = useStickyHeader(hasBreadcrumb, lite);

  const handleClickOutside = () => {
    handleCloseDropdown();
  };
  useOutsideClick(headerRef, handleClickOutside);

  const handleCloseDropdown = () => {
    setSelectedCategory(null);
    setSelected(null);
  };

  useEffect(() => {
    if (selectedCategory) {
      disableScroll();
      showOverlay(true);
    } else {
      enableScroll();
      showOverlay(false);
    }
  }, [selectedCategory]);

  // set a CSS variable with the scrollbar width
  setScrollbarWidthForCSS();

  const openMegaMenu = () => {
    setSelectedCategory('MegaMenu');
  };

  const toggleMegaMenu = (index) => {
    if (selected === index) {
      handleCloseDropdown();
    } else {
      setSelected(index);
      openMegaMenu();
    }
  };

  const toggleMegaMenuMobile = () => {
    if (showMegaMenu) {
      setSelectedCategory(null);
      handleCloseDropdown();
    } else {
      openMegaMenu();
    }
  };

  const toggleSearchArea = () => {
    showSearchArea ? enableScroll() : disableScroll();
    handleCloseDropdown();

    if (selectedCategory !== 'SearchArea') {
      setSelectedCategory('SearchArea');
    }
  };

  const toggleSignInArea = () => {
    handleCloseDropdown();

    if (selectedCategory !== 'SignInArea') {
      setSelectedCategory('SignInArea');
    }
  };

  const toggleMarketArea = () => {
    handleCloseDropdown();

    if (selectedCategory !== 'MarketArea') {
      setSelectedCategory('MarketArea');
    }
  };

  const showOverlay = (value: boolean) => {
    if (value) {
      document.querySelector('.overlay').classList.add('show');
      document.querySelector('.l-header-sticky')?.classList.add('adjust-width');
    } else {
      document.querySelector('.overlay').classList.remove('show');
      document.querySelector('.l-header-sticky')?.classList.remove('adjust-width');
    }
  };

  // UserStory : 324368 Removed Geo-banner from production
  // const closeGeolocation = () => {
  //   handleScroll();
  // };

  return (
    <div ref={headerRef} className={`${cls}-sticky-wrapper`} id={`${cls}-sticky-wrapper`}>
      <div className={stickyCss}>
        {/* UserStory : 324368 Removed Geo-banner from production */}
        {/* <Geolocation onCloseGeolocation={closeGeolocation} /> */}
        {!lite && <TopMenu topMenu={topMenu} />}

        <div className={`${cls}-main`}>
          <div className={`${cls}-container ${isLoadingMegaMenuNavigation ? 'loading' : ''}`}>
            <div className={`${cls}-main-left`}>
              <div className={`${cls}-main-mobile-item`}>
                <div
                  className={activeClass('c-animation-box', showMegaMenu)}
                  onClick={toggleMegaMenuMobile}
                >
                  <ul>
                    <li>
                      <i className='svg-icon icon-symbol-close'></i>
                    </li>
                    <li>
                      <i className='svg-icon icon-symbol-burger'></i>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <Brand className={`${cls}-main-brand`} />
            {megaMenuNavigation && (
              <div className={`${cls}-main-right`}>
                <MenuSection
                  showSignInArea={showSignInArea}
                  showSearchArea={showSearchArea}
                  showMarketArea={showMarketArea}
                  toggleMegaMenu={toggleMegaMenu}
                  toggleSearchArea={toggleSearchArea}
                  toggleSignInArea={toggleSignInArea}
                  toggleMarketArea={toggleMarketArea}
                  megaMenuNavigation={megaMenuNavigation.MainMenuLinks}
                  selected={selected}
                  searchBtnText={megaMenuNavigation.Search.Name}
                  signInBtnText={megaMenuNavigation.SignIn.Heading}
                  signInBtnLinks={megaMenuNavigation.SignIn.Links}
                  signInDescription={megaMenuNavigation.SignIn.Description}
                  showHideSignIn={megaMenuNavigation.SignIn.HideSignIn}
                  contactBtnText={megaMenuNavigation.Contact.Text}
                  contactLink={megaMenuNavigation.Contact.Href}
                  cls={cls}
                />
              </div>
            )}
          </div>
        </div>
        <div className={`${cls}-dropdown`}>
          <div
            className={classNames(
              `${cls}-dropdown-container`,
              showDropDown ? 'show' : '',
              showSignInArea ? 'sign-in-dropdown' : ''
            )}
          >
            {isDesktop && !isMobileScreen && (
              <MegaMenuMain
                megaMenuColumns={megaMenuNavigation?.MegaMenuColumns || {}}
                selected={selected}
                show={showMegaMenu}
                closeMegaMenu={handleCloseDropdown}
              />
            )}

            {showSearchArea && (
              <div className='l-mega-menu-search'>
                <div className='l-search-area'>
                  <div className='l-search-area-container'>
                    <div className='l-search-area-last'>
                      <SearchBar
                        className='l-search-area-input-wrapper'
                        placeholder={megaMenuNavigation.Search.Placeholder}
                        showSearchIcon={false}
                        onSubmit={(value) => {
                          const searchUrl = megaMenuNavigation.Search.SearchUrl;
                          const searchParams = new URLSearchParams({
                            query: value,
                            category: 'All',
                            orderBy: 'relevance',
                          });
                          window.location.href = `${searchUrl}?${searchParams.toString()}`;
                        }}
                      />
                    </div>
                    <CloseButton className='search-close' onClick={handleCloseDropdown} />
                  </div>
                </div>
              </div>
            )}

            {!(megaMenuNavigation?.SignIn.HideSignIn) && selectedCategory === 'SignInArea' && (
              <div className='l-mega-menu-sign-in'>
                <div className='l-sign-in-area-container'>
                  <SignInDropdown
                    signInInformation={megaMenuNavigation?.SignIn.Description}
                    links={megaMenuNavigation?.SignIn.Links}
                  />
                  <CloseButton className='c-close-button' onClick={handleCloseDropdown} />
                </div>
              </div>
            )}

            {showMarketArea && (
              <div className='c-country-selector'>
                <CountrySelector handleCloseDropdown={handleCloseDropdown} />
              </div>
            )}

            {(!isDesktop || isMobileScreen) && megaMenuNavigation && (
              <MainNavigationMobileNew
                columns={megaMenuNavigation.MegaMenuColumns || {}}
                contact={megaMenuNavigation.Contact}
                i18n={megaMenuNavigation.I18n}
                navigation={megaMenuNavigation.MainMenuLinks}
                showing={showMegaMenu}
                topMenu={topMenu}
                showHideSignIn={megaMenuNavigation.SignIn.HideSignIn}
                signInInformation={megaMenuNavigation?.SignIn.Description}
                links={megaMenuNavigation?.SignIn.Links}
              />
            )}
          </div>
        </div>
        {showHeaderBar && <HeaderBar subNavigation={subNavigation} />}
      </div>
    </div>
  );
};

export default Header;
